import BaseModel from "../models/BaseModel";
import Point from "../models/Point";
import Signal from "../models/Signal";
import BaseModelMap from "../models/BaseModelMap";
import GroupAddress from "../models/GroupAdress";
import { createGlobalAddresses } from "../addressGeneration/createGlobalAddresses";
import Device from "../models/Device";
import { TAG_IGNORE, TAG_MANUAL_SIGNALS, TAG_SPECIAL } from "../models/Constants";
import Channel from "../models/Channel";
import { ChannelType } from "../models/ChannelType";
import { itx } from "../translationItx/itx";
import { showToast } from "./Toasts";

function connectSignalWithNewGroupAddressToSignal(device: Device, signal: Signal): boolean {
  // Stelle sicher, dass wir mit dem Request-Signal arbeiten
  let requestSignal = signal.isRequestSignal ? signal : signal.requestSignal;
  if (!requestSignal) {
    return false;
  }

  ensureSignalInManualChannel(device, signal);

  const isLogicSignal = requestSignal.isLogicSignal;
  const a2 = isLogicSignal ? "3" : "2";

  // Stelle sicher, dass die globalen Adressen existieren
  let manualGroup = state.addresses.findFirst((a) => a.address == "31")?.children.findFirst((a) => a.address == a2);

  if (!manualGroup) {
    createGlobalAddresses(state.settings.knxGroupAddressGeneneration);
    manualGroup = state.addresses.findFirst((a) => a.address == "31")?.children.findFirst((a) => a.address == a2);
  }

  if (!manualGroup) {
    return false;
  }

  // Generiere einen Namen für die Gruppenadresse
  let name = device?.tag + ":" + requestSignal.number + ":" + requestSignal.name + " " + requestSignal.knxFunctionText;
  // name has a max of 32 characters, so we need to truncate it if it's too long
  if (name.length > 32) {
    name = name.substring(0, 29) + "...";
  }
  // Erstelle die neue Gruppenadresse
  const newGroupAddress = GroupAddress.createNewGroupAddress({
    parent: manualGroup,
    level: 3,
    name: name,
  });

  newGroupAddress.updateFullAddress();

  requestSignal.connectWith(newGroupAddress);

  manualGroup.children.add(newGroupAddress);
  state.selectionManager.selectedObject = newGroupAddress;

  return true;
}

/**
 * Ensures a signal that lives in the IGNORE or SPECIAL channel is moved to the
 * ManualSignals channel (request side + KNX side).  Call this whenever a group
 * address is manually assigned to a signal.
 */
export function ensureSignalInManualChannel(device: Device, signal: Signal): boolean {
  const requestSignal = signal.isRequestSignal ? signal : signal.requestSignal;
  if (!requestSignal) return false;

  const requestChannel = requestSignal.parent as Channel;
  const tag = requestChannel.tag;

  // Only act on signals that currently live in IGNORE or SPECIAL channels
  if (tag !== TAG_IGNORE && tag !== TAG_SPECIAL) return false;

  // Create or find the ManualSignals request channel
  const manualRequestChannel = device.channels.getOrCreateItemByTag(TAG_MANUAL_SIGNALS);
  manualRequestChannel.channelType = ChannelType.ManualSignals;
  manualRequestChannel.name = itx("ConnectSignal.manualsignelconnection", "Manuelle Signalverbindungen");

  // Create or find the ManualSignals KNX channel
  let manualKnxChannel = device.knxChannels.getItemByTag(TAG_MANUAL_SIGNALS);
  if (!manualKnxChannel) {
    manualKnxChannel = device.createNewKnxChannel({ tag: TAG_MANUAL_SIGNALS }, device);
    manualKnxChannel.channelType = ChannelType.ManualSignals;
    manualKnxChannel.name = itx("ConnectSignal.manualsignelconnection", "Manuelle Signalverbindungen");
    manualKnxChannel.requestChannel = manualRequestChannel;
    device.knxChannels.add(manualKnxChannel);
  }

  // Link request channel → KNX channel (bi-directional) so that ChannelsSorting
  // can apply the manualChannel filter correctly (only runs for channels with a knxChannel).
  manualRequestChannel.knxChannel = manualKnxChannel;

  // Move request signal to ManualSignals request channel
  requestChannel.signals.remove(requestSignal);
  requestSignal.parent = manualRequestChannel;
  // mapped channel signal remove and add to other channel wird eh neu berechnet beim neu öffnen ^^

  const mappedChannel = requestChannel.mappedChannel;
  if (mappedChannel) {
    if (mappedChannel.signals.asArray().includes(requestSignal)) {
      mappedChannel.signals.remove(requestSignal);
      manualKnxChannel.signals.add(requestSignal);
    } else {
      // other signal in mapped channel?
      // get same signal from mapped signal
      const mappedSignal = mappedChannel.signals.asArray().find((s) => s.number == requestSignal.number);
      if (mappedSignal) {
        mappedChannel.signals.remove(mappedSignal);
        manualKnxChannel.signals.add(mappedSignal);
      }
    }
  }
  manualRequestChannel.signals.add(requestSignal);

  // Move KNX signal to ManualSignals KNX channel (only for KNX signals)
  if (!signal.isRequestSignal) {
    const oldKnxChannel = signal.parent as Channel;
    oldKnxChannel.signals.remove(signal);
    signal.parent = manualKnxChannel;
    manualKnxChannel.signals.add(signal);
  }
  showToast({
    text: itx("ConnectSignal.movedToManualChannel", "Signal #num '#name' wurde in 'Manuelle Signale' verschoben.")
      .replace("#num", requestSignal.number ?? "")
      .replace("#name", requestSignal.name ?? ""),
  });
  device.raiseOnChange("manualSignalConnection");

  return true;
}

/**
 * Moves a signal (request + its KNX counterpart) back to the TAG_IGNORE channel
 * if it no longer has any group addresses assigned.
 * This is the reverse of the connect operation in connectSignalWithNewGroupAddressToSignal.
 */
export function moveSignalBackToIgnoreIfNoGroupAddresses(device: Device, signal: Signal): boolean {
  // Work on the request signal
  const requestSignal = signal.isRequestSignal ? signal : signal.requestSignal;
  if (!requestSignal) return false;

  const requestChannel = requestSignal.parent as Channel;
  if (requestChannel.channelType !== ChannelType.ManualSignals) return false;

  // Only move back if no group addresses remain
  if (requestSignal.groupAddresses.items.length > 0) return false;

  // Retrieve the KNX signal BEFORE reparenting (while parent channel mapping is still intact)
  const knxSignal = requestSignal.knxSignal;

  // Find the IGNORE request channel
  const ignoreRequestChannel = device.channels.getItemByTag(TAG_IGNORE);
  if (!ignoreRequestChannel) return false;

  // Move request signal back to IGNORE request channel
  requestChannel.signals.removeWithoutUpdate(requestSignal);
  requestChannel.signals.raiseOnChangeUI();
  requestSignal.parent = ignoreRequestChannel;
  ignoreRequestChannel.signals.addWithoutUpdate(requestSignal);
  ignoreRequestChannel.signals.raiseOnChangeUI();

  let manualKnxChannel: Channel | undefined;
  let ignoreKnxChannel: Channel | undefined;

  // Move KNX signal back to IGNORE KNX channel
  if (knxSignal) {
    ignoreKnxChannel = device.knxChannels.getItemByTag(TAG_IGNORE);
    if (ignoreKnxChannel) {
      manualKnxChannel = knxSignal.parent as Channel;
      manualKnxChannel.signals.removeWithoutUpdate(knxSignal);
      manualKnxChannel.signals.raiseOnChangeUI();
      knxSignal.parent = ignoreKnxChannel;
      ignoreKnxChannel.signals.addWithoutUpdate(knxSignal);
      ignoreKnxChannel.signals.raiseOnChangeUI();
    }
  }

  return true;
}

export default function ConnectSignal(props: { device: Device; signal: Signal }) {
  const { device, signal } = props;

  function handleClick(event: any, point?: Point) {
    const disabledContextMenuItems: any = [];

    const contextMenuItems: any = {
      title: state.translate("Low Level verbinden"),
      items: [],
    };

    contextMenuItems.items.push(
      {
        id: `coppy`,
        image: (
          <i
            className="las la-clipboard"
            style={{
              height: "15px",
              width: "15px",
              paddingTop: "1px",
            }}
          />
        ),
        label: itx("ConnectSignal.remembersignal", "Dieses Signal zum Verbinden merken"),
        onClick: () => {
          state.clipboardItems = new BaseModelMap<BaseModel>(BaseModel, undefined, undefined);
          state.clipboardItems?.addWithoutUpdate(signal);
        },
      },
      {
        id: `create`,
        image: (
          <i
            className="las la-plus"
            style={{
              height: "15px",
              width: "15px",
              paddingTop: "1px",
            }}
          />
        ),
        label: itx("ConnectSignal.groupaddressforsignal", "Gruppenadresse für dieses Signal erstellen"),
        onClick: () => {
          connectSignalWithNewGroupAddressToSignal(device, signal);
        },
      },
    );

    if (state.clipboardItems && state.clipboardItems.size == 1) {
      const item = state.clipboardItems.asArray()[0];

      if (item.className == "Signal" || (item.className == "GroupAddress" && (item as GroupAddress).level == 3)) {
        const clipboardSignal = item as Signal;

        contextMenuItems.items.push({
          id: `link`,
          image: (
            <i
              className="las la-link"
              style={{
                height: "15px",
                width: "15px",
                paddingTop: "1px",
              }}
            />
          ),
          label: itx("ConnectSignal.connectwith", "Verbinden mit: ") + clipboardSignal.tag + ":" + clipboardSignal.name,

          onClick: () => {
            signal.connectWith(clipboardSignal);
            ensureSignalInManualChannel(device, signal);
          },
        });
      }
    }

    state.contextMenuManager.show1(event, contextMenuItems, disabledContextMenuItems);
  }

  return (
    <button
      id="connectSignal"
      className="button small circle"
      onClick={(ev: any) => {
        ev.stopPropagation();
        handleClick(ev);
      }}
    >
      <i
        className="las la-link"
        style={{
          height: "15px",
          width: "15px",
          paddingTop: "1px",
        }}
      />
    </button>
  );
}
